import * as React from 'react';
import styled from 'styled-components';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { DataCy } from 'types/dataAttributes';
import { JRCConditionalTooltip } from 'components/Common/JRCConditionalWrapper';
import { JRCTooltipProps } from 'components/JRCTooltip/JRCTooltip';
import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import { JRCInputFieldProps } from '../Common/JRCFormFieldRenderer.types';
import { ButtonColors } from '../../../JRCButton/JRCButton';
import { BUTTON_CONFIG } from '../../../JRCButton/JRCButtonConfig';

type Sizes = 's' | 'm';

const ToggleWrapper = styled.div<{ $width: string | undefined }>`
    ${(props) => (props.$width ? `width:${props.$width};` : ``)}
    display: flex;
    flex-direction: row;
    border: 1px solid ${(props) => props.theme.color.grey2};
    border-radius: 8px;
`;
const Label = styled.label<{
    checked: boolean;
    focus: boolean;
    disabled: boolean | undefined;
    $color: ButtonColors | undefined;
    $size: Sizes;
}>`
    flex: 1;
    display: flex;
    padding: ${(props) => (props.$size === 's' ? `0 4px` : `4px`)};
    border-radius: 8px;
    border: 1px solid transparent;
    margin: -1px;
    justify-content: center;
    cursor: pointer;

    ${(props) =>
        props.disabled &&
        `
            cursor: not-allowed;
            color: ${props.theme.color.grey2};
        `}
    ${(props) =>
        props.disabled &&
        props.checked &&
        `
            color: ${
                props.$color ? props.theme.color[BUTTON_CONFIG.contained[props.$color].color] : props.theme.color.grey3
            };
            border-color: ${
                props.$color ? props.theme.color[BUTTON_CONFIG.contained[props.$color].border] : props.theme.color.grey3
            };
            background-color: ${
                props.$color
                    ? props.theme.color[BUTTON_CONFIG.contained[props.$color].background]
                    : props.theme.color.grey1
            };
        `}

    ${(props) =>
        !props.disabled &&
        (props.checked || props.focus) &&
        `
            ${props.$color && `color:${props.theme.color[BUTTON_CONFIG.contained[props.$color].color]};`};
            background-color: ${
                props.$color
                    ? props.theme.color[BUTTON_CONFIG.contained[props.$color].background]
                    : props.theme.color.grey1
            };
            border: 1px solid ${
                props.$color ? props.theme.color[BUTTON_CONFIG.contained[props.$color].border] : props.theme.color.navy
            };
        `};

    ${(props) =>
        !props.disabled &&
        `
            &:hover {
                ${props.$color && `color:${props.theme.color[BUTTON_CONFIG.contained[props.$color].color]};`};
                background-color: ${
                    props.$color
                        ? props.theme.color[BUTTON_CONFIG.contained[props.$color].background]
                        : props.theme.color.grey1
                };
                border: 1px solid ${
                    props.$color
                        ? props.theme.color[BUTTON_CONFIG.contained[props.$color].border]
                        : props.theme.color.navy
                };
            }
        `}

    input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        z-index: -1;
    }
`;

type ToggleListOption<TFieldValues> = {
    label: string | JSX.Element;
    value: string | number | TFieldValues;
    color?: ButtonColors;
};

export type ToggleProps = DataCy & {
    size?: Sizes;
    width?: string;
    disabled?: boolean;
    options: Array<ToggleListOption<string>>;
};

export type ToggleRawProps<TFieldValues extends FieldValues = FieldValues> = ToggleProps &
    Omit<ControllerRenderProps<TFieldValues>, 'value' | 'ref'> & { ref?: React.ForwardedRef<HTMLInputElement> } & {
        value: string;
        tooltip?: JRCTooltipProps;
    };
const InputToggleRaw = function (
    { dataCy, size = 's', width, options, onChange, disabled, name, value, tooltip }: ToggleRawProps,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    const [valueHighligh, setValueHighligh] = React.useState<string | number | undefined>(undefined);
    return (
        <JRCConditionalTooltip condition={!!tooltip} tooltip={tooltip}>
            <ToggleWrapper data-cy={dataCy} data-value={value} $width={width}>
                {options.map((option, i) => {
                    return (
                        <Label
                            key={i}
                            htmlFor={`${name}-${i}`}
                            disabled={disabled}
                            checked={!valueHighligh && value === option.value}
                            focus={valueHighligh === option.value}
                            $color={option.color}
                            $size={size}
                            onMouseEnter={() => {
                                if (disabled) {
                                    return;
                                }
                                setValueHighligh(option.value);
                            }}
                            onMouseLeave={() => {
                                if (disabled) {
                                    return;
                                }
                                setValueHighligh(undefined);
                            }}>
                            <input
                                ref={ref}
                                name={name}
                                id={`${name}-${i}`}
                                type="radio"
                                disabled={disabled}
                                checked={value === option.value}
                                value={option.value}
                                onChange={() => {
                                    if (disabled) {
                                        return;
                                    }
                                    onChange(option.value);
                                }}
                                onFocus={() => {
                                    if (disabled) {
                                        return;
                                    }
                                    setValueHighligh(option.value);
                                }}
                                onBlur={() => {
                                    if (disabled) {
                                        return;
                                    }
                                    setValueHighligh(undefined);
                                }}
                            />
                            {option.label}
                        </Label>
                    );
                })}
            </ToggleWrapper>
        </JRCConditionalTooltip>
    );
};
export const JRCInputToggleRaw = React.forwardRef(InputToggleRaw);

export type JRCInputToggleProps<TFieldValues extends FieldValues = FieldValues> = ToggleRawProps<TFieldValues> &
    JRCInputFieldProps<TFieldValues>;

export function JRCInputToggle<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    rules,
    label,
    htmlFor,
    labelIsLegend,
    description,
    helper,
    ...props
}: JRCInputToggleProps<TFieldValues>) {
    return (
        <JRCFormFieldRenderer
            margin={props.margin}
            name={name}
            control={control}
            rules={rules}
            label={label}
            htmlFor={htmlFor}
            labelIsLegend={labelIsLegend || true}
            description={description}
            helper={helper}
            renderFunction={({ value, ...field }) => <JRCInputToggleRaw {...props} {...field} value={value} />}
        />
    );
}
